import React, { useState } from "react";
import { ClaimsRenderingSection } from "./ClaimsRenderingSection";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Plus, Trash2, Loader2, MapPin, X } from "lucide-react";
import { api, describeError } from "@/lib/api";
import {
  ScreenHead, Section, ListCard, Empty, Skeletons, Avatar,
  GapList, PS_INPUT, titleCase, useProviders, useClients,
} from "./psPrimitives";
import { usePsClient, ClientPicker } from "./psMasters";

export function ProvidersScreen({ mode, onOpenDq }) {
  const qc = useQueryClient();
  const { data: providers = [], isLoading } = useProviders();
  const { data: clients = [] } = useClients();
  const pc = usePsClient();
  // This client's providers plus orphan NPIs (not linked to any client).
  const mine = providers.filter((p) => pc.cid && (String(p.client_id || "") === String(pc.cid) || !p.client_id));
  const [npi, setNpi] = useState("");
  const [lookup, setLookup] = useState(null);
  const [confirm, setConfirm] = useState(null); // provider pending removal

  const lookupMut = useMutation({
    mutationFn: async (n) => (await api.post("/api/edi/note-to-claim/providers/lookup", { npi_number: n })).data,
    onSuccess: (d) => setLookup(d),
    onError: (e) => { setLookup(null); toast.error(describeError(e)); },
  });
  const addMut = useMutation({
    mutationFn: async (p) => {
      const exists = providers.some((x) => String(x.npi_number) === String(p.npi_number));
      const d = exists ? null : (await api.post("/api/edi/note-to-claim/providers", p)).data;
      if (pc.cid) await api.patch(`/api/edi/note-to-claim/providers/${p.npi_number}/client`, { client_id: String(pc.cid) });
      return d;
    },
    onSuccess: () => { setLookup(null); setNpi(""); qc.invalidateQueries({ queryKey: ["providers"] }); },
    onError: (e) => toast.error(describeError(e)),
  });
  const linkMut = useMutation({
    mutationFn: async ({ npiN, client_id }) => (await api.patch(`/api/edi/note-to-claim/providers/${npiN}/client`, { client_id })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["providers"] }),
    onError: (e) => toast.error(describeError(e)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/api/edi/note-to-claim/providers/${id}`)).data,
    onSuccess: () => { setConfirm(null); qc.invalidateQueries({ queryKey: ["providers"] }); },
    onError: (e) => toast.error(describeError(e)),
  });

  const clean = npi.replace(/\D/g, "");
  const canLookup = clean.length === 10 && !lookupMut.isPending;

  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Your Account · step 3 of 4" title="Providers · NPI"
        lede="Look up each rendering provider's NPI — we prefill name, taxonomy, license and address. Providers you add are linked to the client below." />
      {pc.clients.length ? <ClientPicker {...pc} /> : null}

      <form onSubmit={(e) => { e.preventDefault(); if (canLookup) lookupMut.mutate(clean); }} className="flex items-end gap-3 mb-4">
        <div className="flex flex-col gap-0.5 flex-1">
          <label className="lf-label">NPI number</label>
          <input className={PS_INPUT} placeholder="10 digits" value={npi} maxLength={10} inputMode="numeric" data-testid="ps-npi-input" onChange={(e) => setNpi(e.target.value)} />
        </div>
        <button type="submit" disabled={!canLookup} data-testid="ps-npi-lookup" className="btn-ink inline-flex items-center gap-1.5">
          {lookupMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Search size={14} />} Look up
        </button>
      </form>

      {lookup ? (
        <div data-testid="ps-npi-result" className="mb-4 rounded-[14px] border border-[#E9E9EC] bg-white p-4 fade-up">
          <div className="flex items-center gap-3">
            <Avatar text={lookup.provider_name} dark />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[var(--ink)]">{lookup.provider_name}</div>
              <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">NPI {lookup.npi_number} · {lookup.provider_type}</div>
            </div>
            <button onClick={() => addMut.mutate(lookup)} disabled={addMut.isPending} data-testid="ps-provider-add" className="btn-ink inline-flex items-center gap-1.5">
              {addMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={14} />} Add
            </button>
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-[var(--ink2)]">
            <MapPin size={12} className="mt-0.5 text-[var(--gray2)]" />
            {[lookup.address?.address_1, lookup.address?.city, lookup.address?.state, lookup.address?.postal_code].filter(Boolean).join(", ")}
          </div>
        </div>
      ) : null}

      {isLoading || pc.isLoading ? <Skeletons /> : mine.length === 0 ? <Empty><strong className="text-[var(--ink2)]">No providers linked to this client yet.</strong> Look up an NPI above to add one.</Empty> : (
        <ListCard testid="ps-providers-list">
          {mine.map((p, i) => (
            <div key={p.id} data-testid={`ps-provider-row-${p.npi_number}`} className={`px-4 py-3.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
              <div className="flex items-center gap-3">
                <Avatar text={p.provider_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{p.provider_name}</div>
                  <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">NPI {p.npi_number}{p.state ? ` · ${p.state}` : ""}</div>
                </div>
                <select
                  value={p.client_id || ""}
                  data-testid={`ps-provider-link-${p.npi_number}`}
                  onChange={(e) => linkMut.mutate({ npiN: p.npi_number, client_id: e.target.value })}
                  className="ctl-select max-w-[230px]"
                >
                  <option value="">Not linked</option>
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_id} · {titleCase(c.description || c.name || "")}</option>)}
                </select>
                <button type="button" onClick={() => setConfirm(p)} className="text-[var(--gray3)] hover:text-[#B3261E] transition p-1.5 rounded-md hover:bg-black/[.04]" aria-label="Remove provider" data-testid={`ps-provider-remove-${p.npi_number}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              {/* taxonomies — one line each */}
              <div className="mt-2 ml-[52px] flex flex-col gap-1">
                {(p.taxonomies || []).slice().sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0)).map((t, ti) => (
                  <div key={ti} className="text-[11px] leading-snug">
                    <span className="font-mono text-[var(--ink)]">{t.code}</span>
                    <span className="text-[var(--ink)]">&nbsp;&nbsp;{t.desc}</span>
                    {t.primary ? <span className="ml-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-[5px] bg-black/[.06] text-[var(--gray1)]">Primary</span> : null}
                    {t.license ? <span className="text-[var(--gray3)]">&nbsp;&nbsp;· license {t.license}{t.state ? ` (${t.state})` : ""}</span> : null}
                  </div>
                ))}
                <AddTaxonomy mode={mode} onOpenDq={onOpenDq} />
              </div>
            </div>
          ))}
        </ListCard>
      )}

      <ClaimsRenderingSection providers={mine} all={providers} />

      {mode === "full" && (
        <Section title="Full state · needs API" testid="ps-providers-full">
          <GapList onOpen={onOpenDq} items={[
            { title: "More provider identifiers", desc: "CAQH, PTAN, Medicaid IDs, taxonomy edits", dq: 46 },
            { title: "Link history", desc: "When / by whom a provider was linked", dq: 38 },
          ]} />
        </Section>
      )}

      {confirm ? (
        <ConfirmSheet
          title={`Remove ${confirm.provider_name}?`}
          body={`This deletes the NPI ${confirm.npi_number} and everything attached to it — including their ${(confirm.inclusion_rules || []).length + (confirm.exclusion_rules || []).length} pre-claim rules, note template and exam defaults. Adding the NPI again starts from scratch.`}
          confirmLabel="Remove provider"
          busy={delMut.isPending}
          onConfirm={() => delMut.mutate(confirm.id)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  );
}

function AddTaxonomy({ mode, onOpenDq }) {
  const [open, setOpen] = useState(false);
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#5856D6] hover:underline w-fit mt-0.5">
      <Plus size={11} /> Add taxonomy code
    </button>
  );
  return (
    <span className="inline-flex items-center gap-1.5 mt-0.5">
      <input autoFocus placeholder="207R00000X" className="rounded-[8px] border border-black/[.14] bg-white px-2 py-1 text-[11px] font-mono w-[130px] outline-none focus:border-[#5856D6]" onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }} />
      <button type="button" disabled className="text-[10px] font-semibold px-2 py-1 rounded-[6px] bg-black/[.05] text-[var(--gray2)]">Add</button>
      <button type="button" onClick={() => setOpen(false)} className="text-[var(--gray3)]"><X size={12} /></button>
      {mode === "full" ? <button type="button" onClick={() => onOpenDq?.(42)} className="text-[9px] font-semibold text-[#4646b8]">DQ-42</button> : null}
    </span>
  );
}

export function ConfirmSheet({ title, body, confirmLabel, busy, onConfirm, onCancel, danger = true }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(20,20,18,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div data-testid="ps-confirm-sheet" style={{ background: "#fff", borderRadius: 18, padding: 24, width: "min(440px,96vw)", boxShadow: "0 30px 80px rgba(20,20,18,.3)" }}>
        <div className="text-[15px] font-bold text-[var(--ink)]">{title}</div>
        <p className="text-[12.5px] text-[var(--gray1)] mt-2 leading-relaxed">{body}</p>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onCancel} className="text-[12.5px] font-semibold px-4 py-2 rounded-full border border-black/[.12] text-[var(--ink2)]">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid="ps-confirm-go"
            className="text-[12.5px] font-bold px-4 py-2 rounded-full text-white inline-flex items-center gap-1.5"
            style={{ background: danger ? "#B3261E" : "#141412" }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : null}{confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
